import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Grid,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField, // ✅ إضافة TextField
  InputAdornment, // ✅ إضافة InputAdornment
  FormControl, // ✅ إضافة FormControl
  InputLabel, // ✅ إضافة InputLabel
  Select, // ✅ إضافة Select
  MenuItem, // ✅ إضافة MenuItem
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import InfoIcon from "@mui/icons-material/Info";
import SearchIcon from "@mui/icons-material/Search"; // ✅ إضافة SearchIcon
import ClearIcon from "@mui/icons-material/Clear"; // ✅ إضافة ClearIcon
import { deepPurple } from "@mui/material/colors";
import { getUserInfo, setUserPermissions, updateUserBankPermissions } from "../../../services/Users";
import AddUserRootsForm from "./Permissins/LinkUnlinkRoot/AddUserRootsForm";
import UserBankPermissionsData from "./Permissins/Banks/UserBankPermissionsData";
import { PERMISSION_LABELS, GROUP_LABELS } from "./permissions_ar";
import { useFeatures } from "../../../context/PermissionsAndFeatures/FeaturesContext";

export default function UserProfile({ userId = 1 }) {
  const [user, setUser] = useState(null);
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [localPermissions, setLocalPermissions] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPermissions, setPendingPermissions] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // ✅ نُقل إلى الأعلى
  const [filterStatus, setFilterStatus] = useState("all"); // ✅ نُقل إلى الأعلى

  // تأكيد الحفظ
  // const [confirmOpen, setConfirmOpen] = useState(false);
  // const [pendingPermissions, setPendingPermissions] = useState(null);
  // تحديث الجذور عند تعديلها من الفورم
  const handleRootsUpdate = (newRoots = []) => {
    setUser((u) => ({ ...(u || {}), roots: newRoots }));
  };
  // تحديث الخزن/البنوك عند تعديلها من المكوّن
  const handleBanksUpdate = (newBanks = []) => {
    setUser((u) => ({ ...(u || {}), banks: newBanks }));
  };

  const translateGroupLabel = (g) => {
    if (!g) return g;
    const main = g.split("_")[0].toLowerCase();
    return GROUP_LABELS[main] || GROUP_LABELS[g] || g;
  };

  const translatePermissionKey = (key) => {
    if (PERMISSION_LABELS[key]) return PERMISSION_LABELS[key];
    const wordsMap = {
      add: "إضافة",
      edite: "تعديل",
      delete: "حذف",
      show: "عرض",
      addroot: "ربط بالفرع",
      root: "فرع",
      roots: "فروع",
      banks: "خزن",
      bank: "خزنة",
      kitchens: "مطابخ",
      tables: "طاولات",
      orders: "الطلبات",
      delivery: "توصيل",
      takeaway: "تيك أواي",
      menu: "القائمة",
      customers: "عملاء",
      expenses: "مصروفات",
      employees: "الموظفين",
      users: "المستخدمين",
      preparation: "التحضير",
      screen: "شاشة",
      print: "طباعة",
      api: "API",
      change: "تغيير",
      status: "الحالة",
      make: "إنشاء",
      canacel: "إلغاء",
      invoice: "فاتورة",
      financial: "مالية",
      statistics: "إحصائيات",
      support: "الدعم",
    };
    const parts = key.split("_");
    const translated = parts
      .map((p) => wordsMap[p] || p)
      .join(" ");
    return translated;
  };

  const getGroupKeysFromPermissions = (perms = {}) => {
    const skipKeys = new Set(["id", "user_id", "created_at", "updated_at"]);
    const set = new Set();
    Object.keys(perms).forEach((k) => {
      if (skipKeys.has(k)) return;
      const prefix = (k.split("_")[0] || "other").toLowerCase();
      set.add(prefix);
    });
    return Array.from(set).sort();
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    getUserInfo(userId)
      .then((res) => {
        if (!mounted) return;
        const payload = res?.data?.data || res?.data || res;
        setRaw(res);
        setUser(payload);
        const perms = payload?.permissions || {};
        setLocalPermissions(perms);
        const keys = getGroupKeysFromPermissions(perms);
        const initialExpanded = {};
        keys.forEach((k) => (initialExpanded[k] = true));
        setExpandedGroups(initialExpanded);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message || String(err));
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  // استخلاص حالة ميزة إدارة صلاحيات المستخدمين
  const { hasFeature, featuresLoading } = useFeatures();
  const userRolesPermissionsEnabled = !featuresLoading && hasFeature("user_roles_permissions");

  // أثناء تحميل حالة الميزات: عرض سبينر بسيط (حفاظاً على ترتيب الـ hooks)
  if (featuresLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );

  if (!user)
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        لا توجد بيانات للمستخدم
      </Alert>
    );

  const initials = (user.name || user.username || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const savePermissions = async (nextPermissions) => {
    const prev = localPermissions;
    setLocalPermissions(nextPermissions);
    setSaving(true);
    setError(null);
    try {
      await setUserPermissions(userId, nextPermissions);
      setUser((u) => ({ ...(u || {}), permissions: nextPermissions }));
    } catch (err) {
      setLocalPermissions(prev);
      setError(err?.message || String(err) || "خطأ أثناء حفظ الصلاحيات");
    } finally {
      setSaving(false);
    }
  };

  const getChanges = (next = {}, prev = {}) => {
    const skip = new Set(["id", "user_id", "created_at", "updated_at"]);
    return Object.keys(next)
      .filter((k) => !skip.has(k) && String(next[k]) !== String(prev[k] ?? 0))
      .map((k) => ({ key: k, from: prev[k] ?? 0, to: next[k] ?? 0 }));
  };

  const openConfirm = (nextPermissions) => {
    if (!userRolesPermissionsEnabled) {
      alert("🚫 ميزة إدارة صلاحيات المستخدمين (user_roles_permissions) غير مفعّلة.");
      return;
    }
    const changes = getChanges(nextPermissions, localPermissions);
    if (!changes.length) {
      // لا تغييرات فعليّة => نفذ مباشرة للحالات النادرة
      savePermissions(nextPermissions);
      return;
    }
    setPendingPermissions(nextPermissions);
    setConfirmOpen(true);
  };

  const handleTogglePermission = (permKey) => {
    if (!userRolesPermissionsEnabled) {
      alert("🚫 ميزة إدارة صلاحيات المستخدمين (user_roles_permissions) غير مفعّلة.");
      return;
    }
    const cur = localPermissions?.[permKey] ?? 0;
    const next = { ...(localPermissions || {}), [permKey]: cur ? 0 : 1 };
    openConfirm(next);
  };

  const toggleGroup = (g) => {
    setExpandedGroups((prev) => ({ ...prev, [g]: !prev[g] }));
  };

  const toggleGroupPermissions = (g, enable) => {
    if (!userRolesPermissionsEnabled) {
      alert("🚫 ميزة إدارة صلاحيات المستخدمين (user_roles_permissions) غير مفعّلة.");
      return;
    }
    const next = { ...(localPermissions || {}) };
    Object.keys(next).forEach((k) => {
      if ((k.split("_")[0] || "other").toLowerCase() === g) {
        next[k] = enable ? 1 : 0;
      }
    });
    openConfirm(next);
  };

  const toggleAllPermissions = (enable) => {
    if (!userRolesPermissionsEnabled) {
      alert("🚫 ميزة إدارة صلاحيات المستخدمين (user_roles_permissions) غير مفعّلة.");
      return;
    }
    const next = { ...(localPermissions || {}) };
    Object.keys(next).forEach((k) => {
      if (!["id", "user_id", "created_at", "updated_at"].includes(k)) {
        next[k] = enable ? 1 : 0;
      }
    });
    openConfirm(next);
  };

  const allEnabled =
    Object.keys(localPermissions).length > 0 &&
    Object.values(localPermissions).every((v) => Boolean(v));

  const expandAll = () => {
    const keys = getGroupKeysFromPermissions(localPermissions);
    const next = {};
    keys.forEach((k) => (next[k] = true));
    setExpandedGroups(next);
  };

  const collapseAll = () => {
    const keys = getGroupKeysFromPermissions(localPermissions);
    const next = {};
    keys.forEach((k) => (next[k] = false));
    setExpandedGroups(next);
  };

  // ✅ دالة لفلترة الصلاحيات حسب البحث والحالة
  const getFilteredPermissions = () => {
    const skipKeys = new Set(["id", "user_id", "created_at", "updated_at"]);
    const groups = {};
    
    Object.entries(localPermissions || {}).forEach(([k, v]) => {
      if (skipKeys.has(k)) return;
      const prefix = (k.split("_")[0] || "other").toLowerCase();
      if (!groups[prefix]) groups[prefix] = [];
      groups[prefix].push([k, v]);
    });

    // فلترة حسب البحث والحالة
    const filteredGroups = {};
    Object.entries(groups).forEach(([g, perms]) => {
      const filteredPerms = perms.filter(([permKey, permVal]) => {
        const matchesSearch = 
          translatePermissionKey(permKey).toLowerCase().includes(searchQuery.toLowerCase()) ||
          permKey.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesStatus = 
          filterStatus === "all" ||
          (filterStatus === "enabled" && permVal) ||
          (filterStatus === "disabled" && !permVal);
        
        return matchesSearch && matchesStatus;
      });

      if (filteredPerms.length > 0) {
        filteredGroups[g] = filteredPerms;
      }
    });

    return filteredGroups;
  };

  return (
    <Card elevation={3} sx={{ 
      height: "100%",
      minHeight: "95vh", // ✅ زيادة الارتفاع الأدنى
      display: "flex",
      flexDirection: "column",
    }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: deepPurple[500], width: 56, height: 56 }}>
            {initials}
          </Avatar>
        }
        title={
          <Typography variant="h6" component="div">
            {user.name || user.username}
          </Typography>
        }
        subheader={
          <Box>
            <Typography variant="body2" color="text.secondary">
              {user.username ? `@${user.username}` : ""} · {user.type || ""}
            </Typography>
         
          </Box>
        }
        action={
          <Box>
            <Tooltip title={!userRolesPermissionsEnabled ? "ميزة إدارة الصلاحيات معطّلة" : (allEnabled ? "إلغاء تفعيل الكل" : "تفعيل الكل")}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (saving) return;
                    toggleAllPermissions(!allEnabled);
                  }}
                  disabled={saving || !userRolesPermissionsEnabled}
                >
                  {allEnabled ? <LockOpenIcon /> : <LockIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="فتح الكل">
              <IconButton size="small" onClick={expandAll} disabled={saving}>
                <ExpandMoreIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="إغلاق الكل">
              <IconButton size="small" onClick={collapseAll} disabled={saving}>
                <ExpandLessIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="معلومات">
              <IconButton aria-label="info">
                <InfoIcon />
              </IconButton>
            </Tooltip>
            {/* مؤشر الحفظ */}
            {saving && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Box>
        }
      />

      <CardContent sx={{ 
        display: "flex", 
        flexDirection: "column", 
        flex: 1, // ✅ جعل المحتوى يأخذ المساحة المتبقية
        p: 2,
        overflow: "hidden", // ✅ منع overflow
      }}>
        {/* التابات العلوية لتبديل العرض */}
        <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 2 }}>
          <Tab label="الملف العام" />
          <Tab label="الصلاحيات" />
        </Tabs>

        {/* محتوى التبويب يأخذ المساحة المتبقية */}
        <Box sx={{ 
          flex: 1, 
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}>
          {/* TAB 0: الملف العام => معلومات أساسية + فروع + كاشيرات */}
          {tabIndex === 0 && (
            <Box sx={{ height: "100%", overflow: "auto", pr: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      معلومات أساسية
                    </Typography>
                    <List dense>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText primary="المعرف" secondary={String(user.id ?? "-")} />
                      </ListItem>
                      <Divider />
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText primary="الاسم" secondary={user.name ?? "-"} />
                      </ListItem>
                      <Divider />
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText primary="اسم المستخدم" secondary={user.username ?? "-"} />
                      </ListItem>
                      <Divider />
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText primary="النوع" secondary={user.type ?? "-"} />
                      </ListItem>
                      <Divider />
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText primary="الحالة" secondary={user.status === 1 ? "مفعل" : "غير مفعل"} />
                      </ListItem>
                      <Divider />
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText primary="تاريخ الإنشاء" secondary={user.created_at ?? "-"} />
                      </ListItem>
                      <Divider />
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText primary="آخر تحديث" secondary={user.updated_at ?? "-"} />
                      </ListItem>
                    </List>
                  </Paper>

                  <Box mt={2}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        الفروع المرتبطة ({user.roots?.length || 0})
                      </Typography>

                      {user.roots && user.roots.length > 0 ? (
                        user.roots.map((r) => (
                          <Chip key={r.id} label={r.name} sx={{ m: 0.5 }} variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          لا توجد فروع
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      الكاشيرات
                    </Typography>

                    {user.cashiers && user.cashiers.length > 0 ? (
                      <List dense>
                        {user.cashiers.map((c) => (
                          <ListItem key={c.id} sx={{ py: 0.5 }}>
                            <ListItemText primary={c.name || `كاشير ${c.id}`} secondary={c.description || null} />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        لا توجد كاشيرات
                      </Typography>
                    )
                    }
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* TAB 1: الصلاحيات - استغلال المساحة لعرض أكبر قدر ممكن */}
          {tabIndex === 1 && (
            <Box sx={{ 
              height: "100%", 
              display: "flex", 
              flexDirection: "column", 
              overflow: "hidden",
              minHeight: "600px", // ✅ ارتفاع أدنى للمحتوى
            }}>
              {/* ✅ شريط البحث والفلتر */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, flexShrink: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      placeholder="ابحث عن صلاحية..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchQuery("")}
                              edge="end"
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="filter-status-label">حالة الصلاحيات</InputLabel>
                      <Select
                        labelId="filter-status-label"
                        id="filter-status"
                        value={filterStatus}
                        label="حالة الصلاحيات"
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <MenuItem value="all">جميع الصلاحيات</MenuItem>
                        <MenuItem value="enabled">مفعّلة فقط</MenuItem>
                        <MenuItem value="disabled">معطّلة فقط</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>

              {/* الصلاحيات المفلترة */}
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  flex: 1, // يأخذ المساحة المتبقية
                  display: "flex",
                  flexDirection: "column",
                  // main scroll container for permissions (below the search/filter)
                  overflowY: "auto",
                  minHeight: 0,
                  // visible scrollbar styling
                  "&::-webkit-scrollbar": { width: 10 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0,0,0,0.2)",
                    borderRadius: 2,
                  },
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 1, flexShrink: 0 }}>
                  الصلاحيات (مجموعات)
                </Typography>

                {localPermissions && Object.keys(localPermissions).length ? (
                  (() => {
                    const filteredGroups = getFilteredPermissions();
                    // ترتيب مخصص: ضَع "root"/"roots" أولاً إن وُجدت، ثم بقية المجموعات مرتبة أبجدياً
                    const preferredFirst = ["root", "roots"];
                    let sortedGroupKeys = Object.keys(filteredGroups).sort((a, b) => a.localeCompare(b));
                    // قم بإخراج المفاتيح المفضلة أولاً بالحفاظ على ترتيبها
                    sortedGroupKeys = [
                      ...preferredFirst.filter((p) => sortedGroupKeys.includes(p)),
                      ...sortedGroupKeys.filter((k) => !preferredFirst.includes(k)),
                    ];

                    if (!sortedGroupKeys.length) {
                      return (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            لم يتم العثور على صلاحيات تطابق البحث
                          </Typography>
                        </Box>
                      );
                    }

                    return (
                      <Box sx={{ pr: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                        {sortedGroupKeys.map((g) => (
                          <Accordion
                            key={g}
                            expanded={expandedGroups[g] ?? true}
                            sx={{
                              mb: 1,
                              flexShrink: 0,
                              "& .MuiAccordionSummary-root": { minHeight: 36, px: 1 },
                              "& .MuiAccordionSummary-content": { margin: 0 },
                              "& .MuiAccordionDetails-root": { p: 0.5 },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ px: 1, display: "flex", alignItems: "center", gap: 1 }}
                              onClick={() => toggleGroup(g)}
                            >
                              <Typography sx={{ textTransform: "capitalize", fontWeight: 600, fontSize: 13, flex: 1 }}>
                                {translateGroupLabel(g)} ({filteredGroups[g].length})
                              </Typography>

                              {/* أيقونة تفعيل/تعطيل كل الصلاحيات في المجموعة */}
                              {(() => {
                                 const groupEnabled = filteredGroups[g].every(([permKey]) => Boolean(localPermissions[permKey]));
                                 return (
                                   <Tooltip title={groupEnabled ? "إلغاء تفعيل كل الصلاحيات" : "تفعيل كل الصلاحيات"}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (saving) return;
                                        toggleGroupPermissions(g, !groupEnabled);
                                      }}
                                      disabled={saving}
                                    >
                                      {groupEnabled ? (
                                        <LockOpenIcon fontSize="small" color="success" />
                                      ) : (
                                        <LockIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                   </Tooltip>
                                 );
                               })()}
                            </AccordionSummary>
 
                            <AccordionDetails>
                              <Box>
                                {/* إذا كانت هذه مجموعة الفروع، اعرض نموذج إدارة الفروع أولاً */}
                                {(g === "root" || g === "roots") && (
                                  <Box sx={{ px: 1, py: 1 }}>
                                    <AddUserRootsForm
                                      userId={userId}
                                      roots={user?.roots || []}
                                      onUpdate={handleRootsUpdate}
                                      disabled={saving || !userRolesPermissionsEnabled}
                                    />
                                  </Box>
                                )}
                                {/* إذا كانت هذه مجموعة الخزن، اعرض مكوّن صلاحيات الخزن */}
                                {(g === "bank" || g === "banks") && (
                                  <Box sx={{ px: 1, py: 1 }}>
                                    <UserBankPermissionsData
                                      userId={userId}
                                      banks={user?.banks || []}
                                      onUpdate={handleBanksUpdate}
                                      disabled={saving || !userRolesPermissionsEnabled}
                                    />
                                  </Box>
                                )}
                                 {filteredGroups[g].map(([permKey, permVal]) => (
                                  <Box
                                    key={permKey}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      py: 0.5,
                                      px: 1,
                                      borderBottom: "1px solid rgba(0,0,0,0.04)",
                                      // زيادة حجم الخط للعناصر
                                      fontSize: 14,
                                    }}
                                  >
                                    <Box sx={{ color: "text.primary", fontSize: 14, wordBreak: "break-all", pr: 1 }}>
                                      <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.1 }}>
                                        {translatePermissionKey(permKey)}
                                      </Typography>
                                      <Typography component="span" sx={{ color: "text.secondary", fontSize: 13, ml: 1, lineHeight: 1 }}>
                                        ({permKey})
                                      </Typography>
                                    </Box>
 
                                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                       {/* lock/unlock icon */}
                                       <Tooltip title={!userRolesPermissionsEnabled ? "ميزة إدارة الصلاحيات معطّلة" : (localPermissions[permKey] ? "مفعل - انقر لتعطيل" : "معطل - انقر للتفعيل")}>
                                         <span>
                                           <IconButton
                                             size="small"
                                             onClick={() => {
                                               if (saving || !userRolesPermissionsEnabled) return;
                                               handleTogglePermission(permKey);
                                             }}
                                             disabled={saving || !userRolesPermissionsEnabled}
                                           >
                                             {localPermissions[permKey] ? (
                                               <LockOpenIcon fontSize="small" color="success" />
                                             ) : (
                                               <LockIcon fontSize="small" />
                                             )}
                                           </IconButton>
                                         </span>
                                       </Tooltip>
 
                                       <Chip label={String(localPermissions[permKey])} color={localPermissions[permKey] ? "success" : "default"} size="small" />
                                     </Box>
                                   </Box>
                                 ))}
                               </Box>
                             </AccordionDetails>
                           </Accordion>
                         ))}
                      </Box>
                    );
                  })()
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    لا توجد صلاحيات
                  </Typography>
                )}
              </Paper>
            </Box>
          )}
        </Box>
      </CardContent>

      {/* نافذة تأكيد التغييرات */}
      <Dialog 
        open={confirmOpen} 
        onClose={() => setConfirmOpen(false)} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            maxHeight: "90vh", // ✅ زيادة ارتفاع الـ Dialog
          },
        }}
      >
        <DialogTitle>
          تأكيد تغيّر الصلاحيات
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const changes = getChanges(pendingPermissions || {}, localPermissions);
            return (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  عدد التغييرات: {changes.length}
                </Typography>
                <List dense>
                  {changes.map((c) => (
                    <ListItem key={c.key} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={`${translatePermissionKey(c.key)} (${c.key})`}
                        secondary={`${String(c.from)} → ${String(c.to)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmOpen(false); setPendingPermissions(null); }} disabled={saving}>
            إلغاء
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!pendingPermissions) return;
              setConfirmOpen(false);
              savePermissions(pendingPermissions);
              setPendingPermissions(null);
            }}
            disabled={saving}
          >
            تأكيد ({pendingPermissions ? getChanges(pendingPermissions, localPermissions).length : 0})
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}